import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';
import { MainFormBase } from '../../main-form-base/main-form-base';
import { PolluxFormConfig } from '../../pollux-form-config/pollux-form-config';
import { WorkspaceBase } from '../../workspace-base/workspace-base';
import { propertiesFormConfig } from './properties-form-config';

/**
 * Properties form
 */
@Component({
  selector: 'properties-form',
  templateUrl: './properties-form.component.html',
  styleUrls: ['./properties-form.component.css']
})
export class PropertiesFormComponent extends MainFormBase<any> implements OnInit, OnDestroy {
  /**
   * Project retriever
   */
  @ViewChild('projectRetriever', { static: false })

  /**
   * Sets the project retriever
   */
  set projectRetriever(retriever: DropDownListComponent) {
    if (retriever) {
      this.registerRetrieverComponent(retriever, 'Project');
    }
  }

  /**
   * Show the move to the previous item button
   */
  @Input() showPrevBtn = false;

  /**
   * Show the move to the next item button
   */
  @Input() showNextBtn = false;

  /**
   * Emits an event if the move to previous item button was clicked
   */
  @Output() showPrevious = new EventEmitter<void>();

  /**
   * Emits an event if the move to next item button was clicked
   */
  @Output() showNext = new EventEmitter<void>();

  /**
   * Sets the oid
   */
  @Input() set oid(value: number) {
    this.currentOid = value;
  }

  /**
   * The workspace
   */
  @Input() workspace: WorkspaceBase;

  /**
   * The required ref lists
   */
  protected get requiredReflists(): string[] {
    return [
      'ref_lease_transaction_sublocation',
      'ref_valuation_country',
      'ref_property_type_l3',
      'ref_lease_transaction_building_energy',
      'ref_l2_energycertificate',
      'ref_l2_tenure',
      'ref_statuskoordinate',
      'ref_property_type_l3',
      'config_user'
    ];
  }

  /**
   * Format of dates on form
   */
  protected get dateFormat(): string {
    return 'dd/MM/yyyy';
  }

  /**
   * Format of dates with time on form
   */
  protected get dateTimeFormat(): string {
    return 'dd/MM/yyyy HH:mm';
  }

  /**
   * Int format
   */
  protected integerFormat: NumberFormatOptions = {
    style: "decimal",
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  /**
  * Constructor
  * @param injector The Angular injector
  */
  constructor(protected injector: Injector) {
    super(injector);
  }

  /**
   * Sets the default retriever data
   * @param data
   */
  protected setDefaultRetrieverData(data: any) {
    const retrieverData: any[] = this.retrieverData['Project'];
    //Add the 0 entry
    if (!retrieverData.find((item) => item.id === 0)) {
      retrieverData.splice(0, 0, {
        id: 0,
        projectName: ''
      });
    }

    //Add the pre-selected entry
    const projectId = data.ProjectStdID;
    if (projectId > 0) {
      if (!retrieverData.find((item) => item.id === projectId)) {
        retrieverData.splice(1, 0, {
          id: data.ProjectStdID,
          projectName: data.ProjectStdIDDescription
        });
      }
    }
  }

  /**
   * Gets the required retrievers
   */
  protected get requiredRetrievers(): string[] {
    return ['Project'];
  }

  /**
  * Gets the API POST endpoint for saving a single BO
  */
  protected getSaveSingleEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.comparables.api', `Properties`);
  }

  /**
  * Gets the API GET endpoint for loading a single BO by it's OID
  * @param oid The BO's OID
  */
  protected getLoadSingleEndpoint(oid: number): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.comparables.api', `Properties?oid=${oid}`);
  }

  /**
   * Gets the workspace reference
   */
  public get workspaceReference(): WorkspaceBase {
    return this.workspace;
  }

  /**
   * Closes the form
   */
  public close(): void {
    this.eventBus.broadcastEvent('closeCompPropertiesForm');
  }

  /**
   * Gets the form config
   */
  protected get formConfig(): PolluxFormConfig[] {
    return propertiesFormConfig;
  }

  /**
   * Performs post-processing of the data
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded data
   */
  protected postProcessData(data: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        this.reflistData['ref_property_subtype_l3'] = this.additionalLoadPromisesResult.propertySubtypes;

        const typeId = data.PropertyTypeCode;
        this.reflistData['ref_property_subtype_l3_filtered'] = this.reflistData['ref_property_subtype_l3'].filter(r => r.parentNo === typeId || r.oid === 0);
        this.sortDropdownData(this.reflistData['ref_valuation_country']);
        this.sortDropdownData(this.reflistData['ref_property_type_l3']);

        if (this.isNew) {
          data.Latitude = data.Longitude = null;
        }

        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * If the save call was triggered, this method is called.
   * Use it to inform the user, close the form or trigger grid reloading.
   * @param saveResult Th result returned from the API
   */
  protected handlePostSave(saveResult: any): void {
    this.eventBus.broadcastEvent('propertiesDataSaved', saveResult, this.currentOid === -1);
    this.close();
  }

  /**
   * If an error occurred during saving, this method is called.
   * Use it to re-enable buttons, inform the user, etc.
   * Note: This occurs on errors and doesn't evaluate the saveResult returned from the API, since this can be different between calls.
   * @param error The error information
   */
  protected handleSaveError(error: any): void {
    console.error(error);
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * If the save process was aborted, this methd is called.
   * You can use it to re-enable buttons, inform the user, etc
   * @param _data The data that should be saved originally
   */
  public handleSaveAborted(_data: any): void {
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * Perform any pre-save activities here, for example user interaction, user queries etc.
   * If the Promise is resolved with true, saving will continue. If iit's resolved with false or rejected, saving is cancelled.
   * @param _data The data that should be saved
   */
  public handlePreSave(_data: any): Promise<boolean> {
    return Promise.resolve(true);
  }

  /**
   * If a validation error occurs, this method is called.
   * Use it to inform the user, enable buttons, etc
   * @param _data The data
   */
  public handleValidationError(_data: any) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      hideConfirmButton: true,
      cancel: () => {
        //Nothing
      }
    }, 'fillRequiredFieldsDialog');
  }

  /**
   * Destroys the component
   */
  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    // we need parentNo for ref_property_subtype_l3
    const propertySubtypesUrl = this.appServiceHelper.getServiceEndpoint('pollux.api', `reflist/pollux?reflistName=ref_property_subtype_l3&withParentNo=true`);
    this.getObservableOnLoad('propertySubtypes', this.httpClient.get(propertySubtypesUrl));

    const onEvents = this.onEvents;

    onEvents.push(this.eventBus.subscribeEvent('savePropertyData', (_parent, _options) => {
      this.save();
    }));

    onEvents.push(this.eventBus.subscribeEvent('cancelPropertyDataChanges', (_payload, _options) => {
      this.close();
    }));

    onEvents.push(this.eventBus.subscribeEvent('valuationTabSelect', (_payload, _options) => {
      this.close();
    }));

    onEvents.push(this.eventBus.subscribeEvent('comparablesTabSelect', (_payload, _options) => {
      this.close();
    }));

    this.init().then(() => {
      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', true);

      //inform infobar if required (in both PD & Comparables) as component is shared
      this.informInfobar('project-valuation-infobar-item-changed');
      this.informInfobar('comparables-infobar-item-changed');
    });
  }

  /**
   * Inform the infobar about changes
   * @param eventName
   */
  private informInfobar(eventName: string) {
    this.eventBus.broadcastEvent(eventName, {
      mode: 'property',
      item: this.formData.getRawValue(),
      itemIdentityProperty: 'Oid'
    });
  }

  /**
   * Request the next property to be displayed
   */
  public nextProperty(): void {
    this.showNext.emit();
  }

  /**
   * Request the previous property to be displayed
   */
  public previousProperty(): void {
    this.showPrevious.emit();
  }

  /**
   * Evaluates the save result. Returns true if saving was successful
   * @param result The save result
   */
  public evaluateSaveResult(result: any): boolean {
    return result?.m_Item1;
  }

  /**
   * propertyTypeChanged event handler
   */
  public propertyTypeChanged() {
    if (this.formData) {
      const typeId = parseInt(this.formData.get('PropertyTypeCode').value);

      this.reflistData['ref_property_subtype_l3_filtered'] = null;
      setTimeout(() => {
        this.formData.get('PropertySubTypeCode').setValue(0);
        // filter subtype choices by PropertyTypeCode
        this.reflistData['ref_property_subtype_l3_filtered'] = this.reflistData['ref_property_subtype_l3'].filter(r => r.parentNo === typeId || r.oid === 0);
        this.sortDropdownData(this.reflistData['ref_property_subtype_l3_filtered']);
      });
    }
  }

  /**
   * Apply the project name to the retriever
   */
  public applyProjectName(): void {
    const projectId = this.formData.controls['ProjectStdID'].value;
    const retrieverEntry = this.retrieverData['Project'].find((item) => item.id === projectId);

    if (retrieverEntry) {
      this.formData.controls['ProjectStdIDDescription'].setValue(retrieverEntry.projectName);

    }
    else {
      this.formData.controls['ProjectStdIDDescription'].setValue('');
    }
  }

  /**
   * Sorts dropdown fields by the description field
   * @param array Dropdown data
   */
  sortDropdownData(array: any) {
    array.sort((a, b) => {
      const descriptionA = a.description.toLowerCase();
      const descriptionB = b.description.toLowerCase();
      return descriptionA.localeCompare(descriptionB);
    });
  }
}
